import type { AgingBucket, CollectionStatus, IAgingClient, IAgingInvoice } from 'src/types';

import { openAmountOf, orderCodesOf, termsDaysOf } from './client-aging-labels';

/**
 * NAT-2610 — the client aging table sorts on every data column (standing rule).
 *
 * `priority` has no client-side comparator: the server ranks by a score this table cannot
 * reproduce (each invoice weighed by its own capped age, never-invoiced orders ignored, customers
 * who still buy above dormant ones). Descending is that ranking as sent; ascending is its exact
 * reverse. Every other column sorts on its own value, and a tie keeps the server's order, so rows
 * that compare equal still read in priority.
 */
export type ClientAgingSortField =
  | 'priority'
  | 'clientName'
  | 'agentName'
  | 'totalOverdue'
  | 'worstBucket'
  | 'collectionStatus'
  | 'lastContactedAt'
  | 'invoiceCount'
  | 'paymentDays'
  | 'termsDays';

export type SortOrder = 'asc' | 'desc';

export const CLIENT_AGING_SORT_FIELDS: readonly ClientAgingSortField[] = [
  'priority',
  'clientName',
  'agentName',
  'totalOverdue',
  'worstBucket',
  'collectionStatus',
  'lastContactedAt',
  'invoiceCount',
  'paymentDays',
  'termsDays',
];

export const isClientAgingSortField = (field: string): field is ClientAgingSortField =>
  (CLIENT_AGING_SORT_FIELDS as readonly string[]).includes(field);

const BUCKET_RANK: Record<AgingBucket, number> = {
  current: 0,
  '1_30': 1,
  '31_60': 2,
  '60_plus': 3,
};

/** How far along the chase a client is — the order the collection ladder moves in. */
const STATUS_RANK: Record<CollectionStatus, number> = {
  none: 0,
  da_contattare: 1,
  contattato: 2,
  promessa_pagamento: 3,
  in_contestazione: 4,
  escalato: 5,
};

const statusRank = (status?: CollectionStatus | null) => STATUS_RANK[status ?? 'none'] ?? 0;

/**
 * NAT-2800 — the payment columns have holes: a client with no payment in the window has no days,
 * and one with no terms on file has no terms. A hole sorts after every figure whichever way the
 * column runs, so «—» never crowds out the clients that have something to compare.
 */
const NULLABLE: Partial<Record<ClientAgingSortField, (c: IAgingClient) => number | null>> = {
  paymentDays: (c) => c.paymentTime?.days ?? null,
  termsDays: termsDaysOf,
};

const COMPARE: Record<
  Exclude<
    ClientAgingSortField,
    'priority' | 'clientName' | 'agentName' | 'paymentDays' | 'termsDays'
  >,
  (a: IAgingClient, b: IAgingClient) => number
> = {
  totalOverdue: (a, b) => a.totalOverdue - b.totalOverdue,
  worstBucket: (a, b) => BUCKET_RANK[a.worstBucket] - BUCKET_RANK[b.worstBucket],
  collectionStatus: (a, b) => statusRank(a.collectionStatus) - statusRank(b.collectionStatus),
  // yyyy-MM-dd compares as text; never contacted ('') is the oldest contact of all.
  lastContactedAt: (a, b) => (a.lastContactedAt ?? '').localeCompare(b.lastContactedAt ?? ''),
  invoiceCount: (a, b) => a.invoiceCount - b.invoiceCount,
};

/**
 * `clientName` is the one column whose ordering is language-sensitive, so it needs a collator
 * rather than a fixed entry in `COMPARE` — a bare `localeCompare()` with no locale argument
 * follows the admin's browser, not the app's active language, so the same list can read in a
 * different order for two admins. The caller (the table, which knows the active language) builds
 * the collator and passes it in; this file stays pure and has no opinion on locale.
 */
export function sortClients(
  clients: IAgingClient[],
  field: ClientAgingSortField,
  order: SortOrder,
  collator: Intl.Collator,
): IAgingClient[] {
  if (field === 'priority') return order === 'desc' ? [...clients] : [...clients].reverse();
  const direction = order === 'asc' ? 1 : -1;
  // The name columns are language-sensitive, so they take the collator. The agent column sorts on
  // the label as shown, «Natives (direzionale)» included.
  const byText: Partial<
    Record<ClientAgingSortField, (a: IAgingClient, b: IAgingClient) => number>
  > = {
    clientName: (a, b) => collator.compare(a.clientName, b.clientName),
    agentName: (a, b) => collator.compare(a.agentName ?? '', b.agentName ?? ''),
  };
  const valueOf = NULLABLE[field];
  if (valueOf) {
    return clients
      .map((client, index) => ({ client, index, value: valueOf(client) }))
      .sort((x, y) => {
        if (x.value === null || y.value === null) {
          return (x.value === null ? 1 : 0) - (y.value === null ? 1 : 0) || x.index - y.index;
        }
        return direction * (x.value - y.value) || x.index - y.index;
      })
      .map(({ client }) => client);
  }
  const compare = byText[field] ?? COMPARE[field as keyof typeof COMPARE];
  return clients
    .map((client, index) => ({ client, index }))
    .sort((x, y) => direction * compare(x.client, y.client) || x.index - y.index)
    .map(({ client }) => client);
}

/** The invoices under a client: every column sorts (standing rule). */
export type InvoiceSortField =
  | 'orderCode'
  | 'invoiceNumber'
  | 'amount'
  | 'invoiceDueDate'
  | 'daysOverdue';

export function sortInvoices(
  invoices: IAgingInvoice[],
  field: InvoiceSortField,
  order: SortOrder,
  collator: Intl.Collator,
): IAgingInvoice[] {
  const direction = order === 'asc' ? 1 : -1;
  const compare = (a: IAgingInvoice, b: IAgingInvoice): number => {
    switch (field) {
      case 'orderCode':
        return collator.compare(orderCodesOf(a).join(', '), orderCodesOf(b).join(', '));
      case 'invoiceNumber':
        return collator.compare(a.invoiceNumber ?? '', b.invoiceNumber ?? '');
      case 'amount':
        return (openAmountOf(a) ?? 0) - (openAmountOf(b) ?? 0);
      case 'invoiceDueDate':
        return (a.invoiceDueDate ?? '').localeCompare(b.invoiceDueDate ?? '');
      default:
        return a.daysOverdue - b.daysOverdue;
    }
  };
  return invoices
    .map((invoice, index) => ({ invoice, index }))
    .sort((x, y) => direction * compare(x.invoice, y.invoice) || x.index - y.index)
    .map(({ invoice }) => invoice);
}
